// 클라이언트 → 달 프록시(/api/hyuh) 호출. Bwek의 기존 Cloud Run(bwek-judge)을 재사용한다.
// 키는 절대 클라이언트에 두지 않는다 — 프록시가 들고 레이트리밋도 건다.
// 실패/타임아웃이면 throw → respond()가 fallbackResponse로 폴백(루프 안 끊김).

import type { Lang, MoonResponse, Register } from "./types";
import type { Moon, MoonInput } from "./moon";
import { buildProphecy, oneLinerFor, pickProphecyType } from "./catalog";

/** 프록시로 보내는 요청. content(고백)는 이 순간에만 전송, 서버는 저장 안 함(무흔적). */
export interface MoonRequest {
  lang: Lang;
  content: string;
  now: string;
}

const REGISTERS: Register[] = ["weary", "heavy", "tender", "light"];

function asRegister(v: unknown): Register {
  return typeof v === "string" && (REGISTERS as string[]).includes(v) ? (v as Register) : "light";
}
/** 서버 응답(JSON) → MoonResponse. 예언 본문은 카탈로그에서 조립(보편적 고정). */
export function validateMoonResponse(data: unknown, lang: Lang, text: string, now: Date): MoonResponse {
  if (typeof data !== "object" || data === null) throw new Error("moon: invalid response shape");
  const d = data as Record<string, unknown>;
  const crisis = d.crisis === true;
  if (crisis) {
    const gate = d.gate === "harm" ? "harm" : "self_crisis";
    return { crisis: true, gate, register: "heavy", letter: "", oneLine: "", prophecy: { type: 1, text: "" } };
  }
  const letter = typeof d.letter === "string" ? d.letter.trim() : "";
  if (!letter) throw new Error("moon: missing letter"); // 편지 없으면 폴백
  const register = asRegister(d.register);
  const type = pickProphecyType(register); // ★종류는 클라가 가중 무작위로 — AI에 맡기면 ④해방으로 쏠림
  // seed는 텍스트 길이 기반(②날짜형 beacon 변주용). 원문 단어는 안 쓴다.
  const seed = (text.length * 2654435761) & 0xffff;
  // 한 마디(Pass B)가 비면 카탈로그로 채운다 → 좋은 AI 편지를 폴백으로 날리지 않는다.
  const oneLine = typeof d.one_line === "string" && d.one_line.trim() ? d.one_line.trim() : oneLinerFor(lang, register, seed);
  return { crisis: false, register, letter, oneLine, prophecy: buildProphecy(type, lang, seed, now) };
}

export interface HttpMoonOptions {
  timeoutMs?: number; // 기본 13000. Cloud Run 콜드스타트 커버(헤아림 연출이 덮음).
  retries?: number; // 기본 1. 1차가 인스턴스를 깨우면 재시도는 빠르게 성공.
  fetchImpl?: typeof fetch;
}

export function httpMoon(endpoint: string, opts: HttpMoonOptions = {}): Moon {
  const doFetch = opts.fetchImpl ?? fetch;
  const timeoutMs = opts.timeoutMs ?? 18000; // 콜드스타트+2패스(웜 ~8s) 여유 — 거짓 타임아웃→더미 폴백 방지.
  const retries = opts.retries ?? 1;
  return {
    async respond(input: MoonInput): Promise<MoonResponse> {
      const req: MoonRequest = { lang: input.lang, content: input.text, now: input.now.toISOString() };
      const body = JSON.stringify(req);
      let lastErr: unknown;
      for (let attempt = 0; attempt <= retries; attempt++) {
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), timeoutMs);
        try {
          const res = await doFetch(endpoint, {
            method: "POST",
            headers: { "content-type": "application/json" },
            body,
            signal: ctrl.signal,
          });
          if (!res.ok) throw new Error(`moon: HTTP ${res.status}`);
          return validateMoonResponse(await res.json(), input.lang, input.text, input.now);
        } catch (e) {
          lastErr = e;
        } finally {
          clearTimeout(timer);
        }
      }
      throw lastErr;
    },
  };
}
